import { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import lightLogo from "../home/logolong.png";
import darkLogo from "../home/OLDlogolong.png";
import styles from "./ClientStudyGuide.module.css";

export default function ClientStudyGuide() {
  const { isDarkMode } = useContext(ThemeContext);
  const [logo, setLogo] = useState(lightLogo);

  useEffect(() => {
    setLogo(isDarkMode ? darkLogo : lightLogo);
  }, [isDarkMode]);

  return (
    <>
      <header>
        <span></span>
        <img src={logo} alt="Linesol" style={{ height: "44px" }} />
        <span></span>
      </header>

      <main className={styles.pageContainer} aria-labelledby="client-study-guide-title">
        <div className={styles.contentWrapper}>
          <h1 id="client-study-guide-title" className={styles.title}>
            Study Design Guide for Clients
          </h1>
          <p className={styles.subtitle}>
            Welcome to our platform! This guide helps you design studies that maximize insights from your
            target audience. By following these guidelines, you'll create effective, reliable studies that
            reveal what drives yes/no responses across demographics. We'll highlight common pitfalls to avoid,
            ensuring your results are actionable and interpretable.
          </p>

          <section className={styles.section} aria-labelledby="platform-overview-title">
            <h2 id="platform-overview-title" className={styles.sectionTitle}>Platform Overview</h2>

            <p className={styles.paragraph}>
              Our platform excels at fast, cost-effective quantitative research on diverse user segments.
              Key strengths include:
            </p>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Key strengths</p>
              <ul className={styles.exampleList}>
                <li>• Analyzing multiple question-card elements like title, description, image, and price alongside user demographics to identify the strongest drivers of positive responses.</li>
                <li>• Speed: 10-second yes/no studies and automated ML data analysis for quick results and iteration.</li>
                <li>• Diversity: Granular, measurable user features avoid ambiguity, such as using specific income ranges instead of vague labels like "high earners".</li>
              </ul>
            </div>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Limitations to keep in mind</p>
              <ul className={styles.exampleList}>
                <li>• Character limits on question-card fields, with stricter limits when images are present.</li>
                <li>• Image uploads must be 20 MB or smaller.</li>
                <li>• Only images are supported. Videos are not supported.</li>
                <li>• Studies must fit a yes/no question format with defined features.</li>
                <li>• Prices are only displayed in $.</li>
              </ul>
            </div>

            <p className={styles.paragraph}>To design strong studies, keep this framing in mind:</p>
            <ul className={styles.exampleList}>
              <li>• Question: Always a binary yes/no question, such as "Would you buy this?"</li>
              <li>• Question-card features: Provide context and vary to test concepts, such as title, description, image, and price. They must be logically coherent and fully express the ideas being tested.</li>
              <li>• User features: Define participant demographics and can be varied for analysis or kept fixed for targeting.</li>
            </ul>
          </section>

          <div className={styles.divider} />

          <section className={styles.section} aria-labelledby="examples-title">
            <h2 id="examples-title" className={styles.sectionTitle}>Examples and Inspiration</h2>

            <p className={styles.paragraph}>
              If you want to see the kinds of studies that fit the platform well, review the examples and case studies at{" "}
              <a className={styles.link} href="https://linesol.ai/#casestudies" target="_blank" rel="noopener noreferrer">
                linesol.ai/#casestudies
              </a>
              .
            </p>
          </section>

          <div className={styles.divider} />

          <section className={styles.section} aria-labelledby="study-structure-title">
            <h2 id="study-structure-title" className={styles.sectionTitle}>Study Structure Essentials</h2>

            <p className={styles.paragraph}>Split features into two groups for clarity.</p>

            <h3 className={styles.subSectionTitle}>A. Question-Card Features</h3>
            <p className={styles.paragraph}>
              These are what participants see. Vary them to test concepts, or keep them constant for building context.
            </p>

            <p className={styles.paragraph}>Supported fields:</p>
            <ul className={styles.exampleList}>
              <li>• Title</li>
              <li>• Image</li>
              <li>• Description</li>
              <li>• Price</li>
            </ul>
            <h3 className={styles.subSectionTitle}>Common representations</h3>
            <ul className={styles.exampleList}>
              <li>• Packaging: vary image.</li>
              <li>• Slogans or copy: vary title and or description.</li>
              <li>• Positioning: vary title, description, and optionally image.</li>
              <li>• Pricing: vary price.</li>
            </ul>
                        <p className={styles.paragraph}>
              <strong>Price rule:</strong> Price must be numeric only, for example <span className={styles.inlineCode}>45</span>, not <span className={styles.inlineCode}>$45</span>.
            </p>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rules for success</p>
              <ul className={styles.exampleList}>
                <li>• Express all visible study variations through these fields.</li>
                <li>• Ensure permutations are logically coherent. Avoid contradictions or nonsensical combinations.</li>
                <li>• Remember that question-card feature categories permute independently. A participant sees one title value, one image value, one description value, and one price value.</li>
                <li>• Avoid incomplete evaluations. If a study is testing concept A and concept B together, every rendered card must show both.</li>
                <li>• Do not assume title, image, and description stay paired. If semantic coherence depends on that pairing, either build the full matrix intentionally or keep the variation inside a single question-card category.</li>
                <li>• If full permutation coverage would create too many values, simplify the study so one question-card category carries the concept variation while the others stay fixed or are removed.</li>
                <li>• Description is participant-facing copy. Remove it when it does not add useful context to the participant's decision.</li>
              </ul>
            </div>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Concept Coverage Rule</p>
              <p className={styles.paragraph}>
                Every rendered card must include all tested concepts. Incomplete coverage confounds results and makes it difficult to isolate effects.
              </p>
              <p className={styles.paragraph}>Example: "Would you buy this?"</p>
              <p className={styles.paragraph}><span className={styles.bad}>Bad:</span> Descriptions mix sourcing and delivery separately, such as some cards showing only "local" and others showing only "fast delivery".</p>
              <p className={styles.paragraph}>Why bad: Respondents evaluate only part of the intended idea, which skews comparisons.</p>
              <p className={styles.paragraph}><span className={styles.good}>Good:</span> Full combinations such as "local + fast", "local + standard", "overseas + fast", and "overseas + standard".</p>
              <div className={styles.jsonBlock}>{`local + fast
local + standard
overseas + fast
overseas + standard`}</div>
              <p className={styles.paragraph}>
                Why good: Each card fully represents the concepts being tested, allowing for clear analysis of their individual and combined effects.
              </p>
              <p>
                If a full matrix would exceed the platform limits or make the study hard to interpret, reduce the number of underlying concepts.
              </p>
            </div>

            <h3 className={styles.subSectionTitle}>B. User Features</h3>
            <p className={styles.paragraph}>
              These are demographic traits used to explain responses. Vary them for audience insights, or keep them fixed for targeting.
            </p>
            <p className={styles.paragraph}>• User features also define who can participate in the study. Only users who match one feature in each user-feature category specified in the study are permitted to answer.</p>
            <p className={styles.paragraph}>• We have measures in place to ensure that overrepresentation of a particular feature is limited to a maximum of 40% above its ideal balanced share within that category.</p>
            <p className={styles.paragraph}>• Example: in a 2-feature category, the ideal split is 50% / 50%, so the maximum allowed imbalance is 70% / 30%. In a 5-feature category, the ideal split is 20% each, so the maximum possible share for one feature is 28%.</p>
                

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rules</p>
              <ul className={styles.exampleList}>
                <li>• Use them as variables, guards, or targeting inputs.</li>
                
                <li>• Treat every user feature as part of a reusable cross-study bank. A person reading the feature outside the current study should still understand the category and every value.</li>
                <li>• Do not use study-local shorthand such as <span className={styles.inlineCode}>High intent</span>, <span className={styles.inlineCode}>Manager</span>, <span className={styles.inlineCode}>Power user</span>, or <span className={styles.inlineCode}>Budget-conscious</span>. Add the missing context in the category name or value labels so the meaning stays stable across studies.</li>
                <li>• Follow the user-features guide for naming and structure: <a className={styles.link} href="https://dashboard.linesol.online/userFeatures-guidelines" target="_blank" rel="noopener noreferrer">dashboard.linesol.online/userFeatures-guidelines</a></li>
                <li>• Custom user features can be created when your study needs something more specific than the standard library. They can be added on <a className={styles.link} href="https://dashboard.linesol.online/form2" target="_blank" rel="noopener noreferrer">dashboard.linesol.online/form2</a>.</li>
                <li>• Composite user features can also be created when there is a clear justification for combining multiple underlying inputs into one higher-level variable. These should be submitted to <a className={styles.link} href="mailto:hello@linesol.online">hello@linesol.online</a> for review.</li>
              </ul>
            </div>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Participation Feasibility Rule</p>
              <p className={styles.paragraph}>
                User-feature targeting must be compatible with the platform's completion balancing.
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>Bad:</span></p>
              <div className={styles.jsonBlock}>{`Professional Industry: Finance; Technology; Sales; Teaching; Hospitality
Professional Work in a Kitchen Hours per Week (width 40): 0-<40; 40-<80`}</div>
              <p className={styles.paragraph}>
                Why bad: With 5 industry values, an even split starts at 20% each. Because overrepresentation is limited to 40% above that ideal share, <span className={styles.inlineCode}>Hospitality</span> can contribute at most <span className={styles.inlineCode}>20% * 1.4 = 28%</span> of completed responses. But with only <span className={styles.inlineCode}>0-&lt;40</span> and <span className={styles.inlineCode}>40-&lt;80</span> kitchen-hours values, the maximum for <span className={styles.inlineCode}>0-&lt;40</span> is <span className={styles.inlineCode}>50% * 1.4 = 70%</span>, so the minimum required for <span className={styles.inlineCode}>40-&lt;80</span> is <span className={styles.inlineCode}>30%</span>. That exceeds the 28% maximum available from <span className={styles.inlineCode}>Hospitality</span>, so the study will never complete.
              </p>
            </div>

            <h3 className={styles.subSectionTitle}>User Feature Naming Standards</h3>
            <p className={styles.paragraph}>Use this format:</p>
            <div className={styles.codeBlock}>[Attribute] [Qualifier] [Measured Aspect] [Units] (width X, start Y)</div>
            <p className={styles.paragraph}><strong>Examples:</strong></p>
            <ul className={styles.exampleList}>
              <li>• <span className={styles.inlineCode}>Salary USD per Year (width 25k)</span></li>
              <li>• <span className={styles.inlineCode}>Age Years (width 10, start 18)</span></li>
              <li>• <span className={styles.inlineCode}>Current Work Role: Individual contributor with no direct reports; People manager; Founder, owner, or executive</span></li>
            </ul>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rules</p>
              <ul className={styles.exampleList}>
                <li>• Keep names concise and avoid repeating units already present in the category name.</li>
                <li>• Continuous bins use half-open ranges, such as <span className={styles.inlineCode}>0-&lt;50k</span>.</li>
                <li>• Discrete bins use integer ranges, such as <span className={styles.inlineCode}>18-27</span> for age.</li>
                <li>• Prefer measurable definitions over vague ones, such as <span className={styles.inlineCode}>Online Purchases per Month (width 4)</span> instead of <span className={styles.inlineCode}>Purchasing Behavior</span>.</li>
                <li>• For categorical features, write values that are explicit on their own. Prefer <span className={styles.inlineCode}>Learn practical skills</span> over <span className={styles.inlineCode}>Learning</span>, and prefer <span className={styles.inlineCode}>Founder, owner, or executive</span> over <span className={styles.inlineCode}>Leadership</span>.</li>
              </ul>
            </div>

            <h3 className={styles.subSectionTitle}>Governance Rules</h3>
            <ul className={styles.exampleList}>
              <li>• Single-select per category. Features within one category must be mutually exclusive.</li>
              <li>• No negations such as <span className={styles.inlineCode}>non-tech</span>. Use separate yes/no categories instead.</li>
              <li>• No catch-all values such as <span className={styles.inlineCode}>Other</span>. Use <span className={styles.inlineCode}>Rather not say</span> if needed.</li>
              <li>• No overlapping ranges, and keep bin widths consistent within a category.</li>
              <li>• Use specific, extensible names that make sense without extra study context.</li>
            </ul>
          </section>

          <div className={styles.divider} />

          <section className={styles.section} aria-labelledby="constraints-title">
            <h2 id="constraints-title" className={styles.sectionTitle}>Platform Constraints</h2>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Content rules</p>
              <ul className={styles.exampleList}>
                <li>• No illegal, harmful, discriminatory, or deceptive content.</li>
                <li>• No collection of sensitive personal data.</li>
                <li>• Maximum 40 features per study, with no duplicates.</li>
                <li>• Question-card permutations must remain logically coherent.</li>
                <li>• Prohibited categories include graphic sexual content, nudity, violence, and weapons.</li>
              </ul>
            </div>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Limits</p>
              <ul className={styles.exampleList}>
                <li>• If no image is present in the question-card features, the current API validation uses these per-field limits: question 100, title 100, description 250, price 24.</li>
                <li>• If any image is present in the question-card features, stricter limits apply per field: question 47, title 47, description 182, price 24.</li>
                <li>• Image uploads must be 20 MB or smaller.</li>
              </ul>
            </div>

            <p className={styles.paragraph}>API and rate limits apply. Requests may be rejected if they exceed validation or service limits.</p>

            <p className={styles.paragraph}>Data use:</p>
            <ul className={styles.exampleList}>
              <li>• Platform-owned outputs are for internal use only.</li>
            </ul>

            <p className={styles.paragraph}><strong>AI note:</strong></p>
            <ul className={styles.exampleList}>
              <li>• Analysis is fully automated, with no per-study human intervention.</li>
            </ul>
          </section>

          <div className={styles.divider} />

          <section className={styles.section} aria-labelledby="quality-title">
            <h2 id="quality-title" className={styles.sectionTitle}>Study Quality Tips</h2>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Recommended sample sizes</p>
              <ul className={styles.exampleList}>
                <li>• Minimum recommended sample: 200.</li>
                <li>• A useful rule of thumb is at least 20 times the number of evaluated features.</li>
                <li>• For enhanced insights: Larger samples (e.g., 500+ participants) often yield more robust findings, better predictive accuracy, and greater confidence in your conclusions.</li>
              </ul>
            </div>

            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Common quality failures</p>
              <ul className={styles.exampleList}>
                <li>• Very low participant counts.</li>
                <li>• Conflicting demographics or weakly defined user features.</li>
                <li>• Infeasible user-feature targeting splits.</li>
                <li>• Heavily skewed yes or no outcomes.</li>
              </ul>
            </div>
          </section>

          <div className={styles.divider} />

          <section className={styles.section} aria-labelledby="checklist-title">
            <h2 id="checklist-title" className={styles.sectionTitle}>Checklist Before Launch</h2>
            <ul className={styles.exampleList}>
              <li>• Review example studies on <a className={styles.link} href="https://linesol.ai/#casestudies" target="_blank" rel="noopener noreferrer">linesol.ai/#casestudies</a> if you are unsure whether your study structure fits the platform well.</li>
              <li>• Keep price numeric.</li>
              <li>• Check the study the way a participant sees it: one value from each question-card category at a time.</li>
              <li>• Make sure all permutations are logically coherent.</li>
              <li>• Maintain full concept coverage rather than partial mixes.</li>
              <li>• Ensure all card features combinations are interpretable within 10 seconds. </li>
              <li>• Keep total features at 40 or fewer, with no duplicates or prohibited content.</li>
              <li>• Make sure user features follow the standards and governance rules.</li>
              <li>• Confirm that only users matching one feature in each specified user-feature category can logically participate.</li>
              <li>• Ensure the requested split is feasible under the 40% overrepresentation limit relative to each category's ideal balanced share.</li>
              <li>• Prefer quantitative, measurable features.</li>
              <li>• Respect maximum character and image upload size limits.</li>
              <li>• Use a realistic sample size.</li>
              <li>• Keep the study aligned with the platform's strengths.</li>
            </ul>
          </section>

          <section className={styles.section} aria-label="support information">
            <p className={styles.paragraph}>
              Follow this guide to avoid common mistakes and get reliable, insightful results. Contact support if you need help at{" "}
              <a className={styles.link} href="mailto:hello@linesol.online">hello@linesol.online</a>.
            </p>
          </section>
        </div>
      </main>
    </>
  );
}